import { useState, useCallback, useMemo } from "react";
import { useShallow } from "zustand/shallow";
import { TagSimpleIcon } from "@phosphor-icons/react";

import { FetchedTag } from "@/services/api/fetchers/tags";
import { useBoundStore } from "@/app/store";
import { TagsGrid } from "@/features/tags/components/TagsGrid";
import { useOpenState } from "@/shared/hooks/useOpenState";
import { ManageTag } from "@/features/tags/components/ManageTag";
import { TagFormValues } from "@/features/tags/components/TagFormContent";
import { ConfirmDeleteTag } from "@/features/tags/components/ConfirmDeleteTag";
import { useTagActions } from "@/features/tags/hooks/useTagActions";
import { usePageLayout } from "@/app/hooks/usePageLayout";

export function TagsRoute() {
  const [selectedTag, setSelectedTag] = useState<FetchedTag | null>(null);
  const { tags, userId } = useBoundStore(useShallow((state) => ({ tags: state.tags.list, userId: state.user.id })));
  const { updateTag, deleteTag, addTag } = useBoundStore(
    useShallow((state) => ({
      updateTag: state.tags.updateTag,
      deleteTag: state.tags.deleteTag,
      addTag: state.tags.addTag,
    }))
  );
  const {
    id: manageTagId,
    isOpen: manageTagIsOpen,
    handleOpenChange: manageTagHandleOpenChange,
  } = useOpenState({ id: "manage-tag" });
  const {
    id: confirmDeleteTagId,
    isOpen: confirmDeleteTagIsOpen,
    handleOpenChange: confirmDeleteTagHandleOpenChange,
  } = useOpenState({ id: "confirm-delete-tag" });
  const [tagToDelete, setTagToDelete] = useState<FetchedTag | null>(null);

  const handleDeleteTag = useCallback(
    (tag: FetchedTag) => {
      setTagToDelete(tag);
      confirmDeleteTagHandleOpenChange(true);
    },
    [confirmDeleteTagHandleOpenChange]
  );

  const tagActions = useTagActions({
    deleteTag: handleDeleteTag,
  });

  const handleCreateTag = useCallback(() => {
    setSelectedTag(null);
    manageTagHandleOpenChange(true);
  }, [manageTagHandleOpenChange]);

  const onTagPress = (id: number) => {
    const tag = tags.find((t) => t.id === id);
    if (tag) {
      setSelectedTag(tag);
      manageTagHandleOpenChange(true);
    }
  };

  const handleSaveTag = async (updatedValues: TagFormValues) => {
    if (selectedTag) {
      updateTag(selectedTag.id, updatedValues);
    } else if (userId) {
      addTag({ color: updatedValues.color, name: updatedValues.name, user_id: userId });
    }
    manageTagHandleOpenChange(false);
  };

  const handleConfirmDeleteTag = useCallback(() => {
    if (tagToDelete) {
      deleteTag(tagToDelete.id);
      setTagToDelete(null);
      confirmDeleteTagHandleOpenChange(false);
    }
  }, [confirmDeleteTagHandleOpenChange, tagToDelete, deleteTag]);

  const pageLayoutConfig = useMemo(
    () => ({ fab: { icon: <TagSimpleIcon />, onPress: handleCreateTag }, navbar: { title: "Tags" } }),
    [handleCreateTag]
  );
  usePageLayout(pageLayoutConfig);

  return (
    <>
      <TagsGrid
        actions={tagActions}
        tags={tags}
        onTagPress={onTagPress}
        isOpen={manageTagIsOpen}
        onOpenChange={manageTagHandleOpenChange}
      />
      <ManageTag
        id={manageTagId}
        initialValues={selectedTag ? { name: selectedTag.name, color: selectedTag.color } : undefined}
        isOpen={manageTagIsOpen}
        onDelete={() => {
          confirmDeleteTagHandleOpenChange(true, { replace: true });
          setTagToDelete(selectedTag);
        }}
        onOpenChange={manageTagHandleOpenChange}
        onSave={handleSaveTag}
      />
      <ConfirmDeleteTag
        id={confirmDeleteTagId}
        isOpen={confirmDeleteTagIsOpen}
        onOpenChange={confirmDeleteTagHandleOpenChange}
        tag={tagToDelete}
        onConfirm={handleConfirmDeleteTag}
      />
    </>
  );
}
